import { useState } from "react";
import { ApiError } from "@/api/client";
import {
  useAlerts,
  useDeleteAlert,
  useSaveAlert,
  type AlertRule,
  type AlertRuleWrite,
  type TrendSeries,
} from "@/api/queries";
import { useFormatter, type ValueFormat } from "@/artifacts/format";
import { describeRule } from "./alertText";
import { Button } from "@/components/ui/Button";
import { Chip, type Tone } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Field } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { Select, Toggle } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { BellIcon, PencilIcon, PlusIcon, SparkIcon, TrashIcon } from "@/components/icons";

/**
 * Threshold alerts on one report or dashboard (ALERT-2).
 *
 * **The metric picker is the trend's own series list.** A series *is* a metric
 * key with a label — the same thing a rule binds to — so a rule can only be
 * made on a number the trend already draws, and the person making it has seen
 * that number move.
 *
 * **Told on transition.** The form says so in its own words, because the
 * question everyone asks next is "will this email me every morning?" — and it
 * will not: one email when the rule starts holding, one when it stops.
 */

type Condition = AlertRuleWrite["condition"];
type Direction = NonNullable<AlertRuleWrite["direction"]>;

const STATE: Record<AlertRule["state"], { tone: Tone; label: string }> = {
  ok: { tone: "success", label: "ok" },
  recovered: { tone: "success", label: "recovered" },
  breached: { tone: "error", label: "breached" },
  metric_missing: { tone: "warning", label: "metric missing" },
  unevaluated: { tone: "neutral", label: "waiting for a snapshot" },
};

const CONDITIONS: Array<[Condition, string]> = [
  ["delta_pct", "changes by a percentage"],
  ["delta_abs", "changes by an amount"],
  ["below", "goes below"],
  ["above", "goes above"],
];

export function AlertState({ rule }: { rule: AlertRule }) {
  const state = STATE[rule.state];
  return <Chip tone={state.tone}>{state.label}</Chip>;
}

function RuleForm({
  subject,
  series,
  rule,
  initialKey,
  onDone,
}: {
  subject: { kind: "report" | "dashboard"; id: string };
  series: TrendSeries[];
  rule: AlertRule | null;
  initialKey?: string | null;
  onDone: () => void;
}) {
  const save = useSaveAlert();
  const [key, setKey] = useState(rule?.metric_key ?? initialKey ?? series[0]?.key ?? "");
  const [condition, setCondition] = useState<Condition>(rule?.condition ?? "delta_pct");
  const [threshold, setThreshold] = useState(String(rule?.threshold ?? 15));
  const [direction, setDirection] = useState<Direction>(rule?.direction ?? "down");
  const [recipients, setRecipients] = useState((rule?.recipients ?? []).join(", "));
  const [recovery, setRecovery] = useState(rule?.notify_recovery ?? true);
  const [name, setName] = useState(rule?.name ?? "");
  const [mode, setMode] = useState<"immediate" | "digest">(rule?.delivery_mode ?? "immediate");

  const chosen = series.find((entry) => entry.key === key);
  // A rule whose metric has left the trend can still be edited: it keeps the
  // binding it had until somebody picks a new one (the rebind).
  const binding = chosen
    ? {
        metric_key: chosen.key,
        metric_label: chosen.label,
        tile_title: chosen.tile_title,
        metric_format: chosen.format,
      }
    : rule
      ? {
          metric_key: rule.metric_key,
          metric_label: rule.metric_label,
          tile_title: rule.tile_title ?? "",
          metric_format: rule.metric_format ?? "number",
        }
      : null;
  const delta = condition === "delta_pct" || condition === "delta_abs";
  const value = Number(threshold);
  const error = save.error instanceof ApiError ? save.error.message : null;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!binding || Number.isNaN(value)) return;
        save.mutate(
          {
            id: rule?.id,
            subject,
            body: {
              ...binding,
              condition,
              threshold: value,
              direction,
              recipients: recipients
                .split(/[,;\s]+/)
                .map((entry) => entry.trim())
                .filter(Boolean),
              notify_recovery: recovery,
              enabled: rule?.enabled ?? true,
              name,
              delivery_mode: mode,
            },
          },
          { onSuccess: onDone },
        );
      }}
    >
      <Select label="Watch" value={key} onChange={(event) => setKey(event.target.value)}>
        {!chosen && rule ? (
          <option value={rule.metric_key}>
            {rule.metric_label} (no longer in the trend — pick its new home)
          </option>
        ) : null}
        {series.map((entry) => (
          <option key={entry.key} value={entry.key}>
            {entry.label}
            {entry.tile_title ? ` · ${entry.tile_title}` : ""}
          </option>
        ))}
      </Select>
      <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
        <Select
          label="Tell me when it"
          value={condition}
          onChange={(event) => setCondition(event.target.value as Condition)}
        >
          {CONDITIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Field
          label={condition === "delta_pct" ? "Percent" : "Amount"}
          type="number"
          step="any"
          value={threshold}
          onChange={(event) => setThreshold(event.target.value)}
        />
      </div>
      {delta ? (
        <Select
          label="Direction"
          value={direction}
          onChange={(event) => setDirection(event.target.value as Direction)}
        >
          <option value="down">Falls</option>
          <option value="up">Rises</option>
          <option value="either">Either way</option>
        </Select>
      ) : null}
      <Field
        label="Email"
        placeholder="Leave empty for the operator address"
        value={recipients}
        onChange={(event) => setRecipients(event.target.value)}
        hint="One email when the rule starts holding, and one when it stops — never one per snapshot."
      />
      <Select
        label="Send"
        value={mode}
        onChange={(event) => setMode(event.target.value as "immediate" | "digest")}
        hint="The digest lists every alert that changed since the day before, in one email."
      >
        <option value="immediate">As it happens</option>
        <option value="digest">In the daily digest</option>
      </Select>
      <Toggle label="Also email when it recovers" checked={recovery} onChange={setRecovery} />
      <Field label="Name (optional)" value={name} onChange={(event) => setName(event.target.value)} />
      {error ? <p className="type-support text-error">{error}</p> : null}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" loading={save.isPending} disabled={!binding}>
          {rule ? "Save alert" : "Create alert"}
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export function AlertsDialog({
  open,
  onClose,
  subject,
  subjectName,
  series,
  seriesLoading,
  initialKey,
  onExplain,
}: {
  open: boolean;
  onClose: () => void;
  subject: { kind: "report" | "dashboard"; id: string };
  subjectName: string;
  series: TrendSeries[];
  seriesLoading: boolean;
  /** Opened from a trend series: start the form on that metric. */
  initialKey?: string | null;
  /** ALERT-3: offered on a breach a report's Cause Lens can explain. */
  onExplain?: (eventId: string) => void;
}) {
  const formatter = useFormatter();
  const { data: rules, isLoading } = useAlerts(subject, open);
  const remove = useDeleteAlert();
  const [editing, setEditing] = useState<AlertRule | "new" | null>(initialKey ? "new" : null);
  const [deleting, setDeleting] = useState<AlertRule | null>(null);
  const format = (value: number, kind: ValueFormat) => formatter.value(value, kind);

  const close = () => {
    setEditing(null);
    onClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={`Alerts on this ${subject.kind}`}
        description={`Evaluated every time “${subjectName}” is snapshotted — by a schedule or by hand.`}
      >
        {editing ? (
          seriesLoading ? (
            <Spinner />
          ) : series.length || editing !== "new" ? (
            <RuleForm
              key={editing === "new" ? "new" : editing.id}
              subject={subject}
              series={series}
              rule={editing === "new" ? null : editing}
              initialKey={initialKey}
              onDone={() => setEditing(null)}
            />
          ) : (
            <p className="type-support text-ink-secondary">
              An alert watches a number the trend draws, and this {subject.kind} has none yet. Take a
              snapshot of a tile that shows a single figure, then come back.
            </p>
          )
        ) : (
          <div className="flex flex-col gap-4">
            {isLoading ? (
              <Spinner />
            ) : rules && rules.length ? (
              <ul className="divide-border border-border rounded-element divide-y border">
                {rules.map((rule) => (
                  <li key={rule.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <div className="min-w-40 flex-1">
                      <p className="type-support text-ink">
                        {rule.name || rule.metric_label} {describeRule(rule, format)}
                      </p>
                      {rule.latest?.note ? (
                        <p className="type-caption text-ink-disabled normal-case">
                          {rule.latest.note}
                        </p>
                      ) : null}
                    </div>
                    <AlertState rule={rule} />
                    {onExplain && rule.latest?.explainable ? (
                      <Button
                        size="sm"
                        icon={<SparkIcon className="size-3.5" />}
                        onClick={() => rule.latest && onExplain(rule.latest.id)}
                      >
                        {rule.latest.cause_lens_message_id ? "Explain again" : "Explain"}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<PencilIcon className="size-3.5" />}
                      onClick={() => setEditing(rule)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<TrashIcon className="size-3.5" />}
                      onClick={() => setDeleting(rule)}
                    >
                      Delete
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="type-support text-ink-secondary flex items-center gap-2">
                <BellIcon className="size-4" />
                No alerts yet.
              </p>
            )}
            <div>
              <Button
                variant="primary"
                icon={<PlusIcon className="size-4" />}
                onClick={() => setEditing("new")}
              >
                New alert
              </Button>
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        loading={remove.isPending}
        title="Delete this alert?"
        description={<>“{deleting?.name || deleting?.metric_label}” will stop being evaluated.</>}
        consequence={<>Its history goes too — every evaluation it recorded.</>}
        onConfirm={() => {
          if (!deleting) return;
          remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
        }}
      />
    </>
  );
}
